// src/pages/admin/AdminUsers.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUsers, getTowns } from '../../lib/db';
import { db, callSetUserRole } from '../../lib/firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [towns, setTowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [townPickerFor, setTownPickerFor] = useState(null); // userId currently choosing a town

  const load = async () => {
    const [u, t] = await Promise.all([getAllUsers(), getTowns()]);
    setUsers(u); setTowns(t); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (userId, role) => {
    try {
      await callSetUserRole({ targetUid: userId, role });
      toast.success('Role updated — takes effect next time they sign in or refresh');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update role');
    }
  };

  const grantTownAdmin = async (userId, townId) => {
    try {
      await callSetUserRole({ targetUid: userId, role: 'town_admin', townId });
      toast.success(`Town Admin granted for ${towns.find(t => t.id === townId)?.name || townId}`);
      setTownPickerFor(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to grant Town Admin');
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}"? This removes their Firestore profile. Their auth account will remain but they will lose all data.`)) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      toast.success('User profile deleted');
      load();
    } catch { toast.error('Failed to delete user'); }
  };

  const filtered = users.filter(u =>
    (!search ||
      u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())) &&
    (roleFilter === 'all' || (u.role || 'user') === roleFilter)
  );

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>Users ({users.length})</div>

      <div style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', borderRadius: 12, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 16, lineHeight: 1.5 }}>
        Town Admin gives someone moderation control over one town's Events, Services, Skill Share, Community, and Scanner — never Sponsors, Users, Analytics, or other towns' data.
      </div>

      <input
        style={s.search}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by name or email..."
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all', 'user', 'organiser', 'town_admin', 'admin'].map(role => (
          <button key={role} onClick={() => setRoleFilter(role)}
            style={{ flex: 1, minWidth: 70, padding: '9px 4px', borderRadius: 10, border: 'none', fontFamily: 'Nunito', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize', background: roleFilter === role ? 'var(--sage-deep)' : 'var(--sage-pale)', color: roleFilter === role ? 'white' : 'var(--sage-dark)' }}>
            {role === 'town_admin' ? 'Town Admin' : role}
          </button>
        ))}
      </div>

      {loading
        ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(u => (
              <div key={u.id} style={s.card}>
                <div style={s.cardTitle}>{u.displayName || '—'}</div>
                <div style={s.cardSub}>{u.email} · {u.defaultTownId || 'no town'}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500, marginBottom: 10 }}>
                  Current role: <strong>{u.role === 'town_admin' ? `Town Admin (${towns.find(t => t.id === u.townId)?.name || u.townId})` : (u.role || 'user')}</strong>
                </div>
                {u.referredBy && (
                  <div style={{ fontSize: 11, color: 'var(--sage-dark)', fontWeight: 600, marginBottom: 10 }}>
                    💌 Referred by: {users.find(x => (x.uid || x.id) === u.referredBy)?.displayName || u.referredBy}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  {['user', 'organiser', 'admin'].map(role => (
                    <button key={role} onClick={() => changeRole(u.uid || u.id, role)}
                      style={{ flex: 1, minWidth: 60, padding: '7px 4px', borderRadius: 10, border: 'none', fontFamily: 'Nunito', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: u.role === role ? 'var(--sage-deep)' : 'var(--sage-pale)', color: u.role === role ? 'white' : 'var(--sage-dark)' }}>
                      {role}
                    </button>
                  ))}
                  <button onClick={() => setTownPickerFor(townPickerFor === (u.uid || u.id) ? null : (u.uid || u.id))}
                    style={{ flex: 1, minWidth: 90, padding: '7px 4px', borderRadius: 10, border: 'none', fontFamily: 'Nunito', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: u.role === 'town_admin' ? 'var(--sage-deep)' : 'var(--sage-pale)', color: u.role === 'town_admin' ? 'white' : 'var(--sage-dark)' }}>
                    Town Admin
                  </button>
                </div>
                {townPickerFor === (u.uid || u.id) && (
                  <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>Grant Town Admin for which town?</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {towns.map(t => (
                        <button key={t.id} onClick={() => grantTownAdmin(u.uid || u.id, t.id)}
                          style={{ padding: '6px 12px', borderRadius: 100, border: 'none', fontFamily: 'Nunito', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', background: 'var(--sage-dark)', color: 'white' }}>
                          {t.name}
                        </button>
                      ))}
                      {towns.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>No towns found — add one in Admin → Towns first.</div>}
                    </div>
                  </div>
                )}
                <button onClick={() => handleDelete(u.uid || u.id, u.displayName || u.email)} style={s.deleteBtn}>
                  Delete profile
                </button>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No users found</div>}
          </div>
      }
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  search: { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9', fontSize: 14, fontFamily: 'Nunito', marginBottom: 16, boxSizing: 'border-box', background: 'var(--white)' },
  card: { background: 'var(--white)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 },
  cardSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 6 },
  deleteBtn: { width: '100%', background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
};

export default AdminUsers;
